import { asStringArray, unwrap } from 'components/AiCompetitiveIntelligence/utils';
import {
  AICompetitiveIntelligenceRankingRow,
  AICompetitiveIntelligenceType,
  AIInsightSolution,
  RecommendationDetailsType,
  SpeedToLeadEventMetric,
} from 'types/recommendations';
import { parseStructureFromJSON } from 'utils/helpers/object';

import {
  SPEED_TO_LEAD_CHANNEL,
  SPEED_TO_LEAD_CHANNEL_LABELS,
  SPEED_TO_LEAD_SECONDS_PER_HOUR,
  SPEED_TO_LEAD_SECONDS_PER_MINUTE,
  SPEED_TO_LEAD_SUMMARY_MAX_ITEMS,
} from './constants';

export type OverallInsight = {
  data: AICompetitiveIntelligenceType;
  rankingRows: AICompetitiveIntelligenceRankingRow[];
};

export const formatDuration = (seconds: number | null) => {
  if (seconds == null) {
    return 'No Attempt';
  }

  const hours = Math.floor(seconds / SPEED_TO_LEAD_SECONDS_PER_HOUR);
  const minutes = Math.floor((seconds % SPEED_TO_LEAD_SECONDS_PER_HOUR) / SPEED_TO_LEAD_SECONDS_PER_MINUTE);
  if (hours === 0 && minutes === 0) {
    return `${seconds}s`;
  }
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
};

export const formatOrdinal = (rank: number | null) => {
  if (rank == null) return '—';
  const remainder = rank % 100;
  if (remainder >= 11 && remainder <= 13) return `${rank}th`;
  return `${rank}${rank % 10 === 1 ? 'st' : rank % 10 === 2 ? 'nd' : rank % 10 === 3 ? 'rd' : 'th'}`;
};

export const getEventChannel = (event: SpeedToLeadEventMetric) => {
  if (
    event.channel === SPEED_TO_LEAD_CHANNEL.CALL &&
    event.outcome?.toLowerCase() === SPEED_TO_LEAD_CHANNEL.VOICEMAIL
  ) {
    return SPEED_TO_LEAD_CHANNEL.VOICEMAIL;
  }

  return event.channel ?? 'No activity';
};

export const getEventChannelLabel = (event: SpeedToLeadEventMetric) =>
  SPEED_TO_LEAD_CHANNEL_LABELS[getEventChannel(event)] ?? getEventChannel(event);

export const getChannelLabel = (channel: string) => SPEED_TO_LEAD_CHANNEL_LABELS[channel] ?? channel;

export const getOverallInsight = (
  recommendation: RecommendationDetailsType | undefined,
  communityId: number | undefined,
  communityName: string | undefined
): OverallInsight | null => {
  const overview = recommendation?.community_overviews.find((item) => item.community_id === communityId);
  const structure = overview ? (parseStructureFromJSON(overview.solution) as AIInsightSolution | false) : false;
  if (!structure) {
    return null;
  }

  const rankingValue = unwrap(structure.ranking);
  const rankingRows = (Array.isArray(rankingValue) ? rankingValue : [])
    .filter((row: unknown): row is AICompetitiveIntelligenceRankingRow & { rank: number; score: number } => {
      if (typeof row !== 'object' || row === null) {
        return false;
      }

      const candidate = row as AICompetitiveIntelligenceRankingRow;
      return (
        typeof candidate.rank === 'number' &&
        typeof candidate.community === 'string' &&
        typeof candidate.score === 'number'
      );
    })
    .sort((left, right) => left.rank - right.rank);
  const primaryRow = rankingRows.find((row) => row.community === communityName);
  const competitorScores = rankingRows.filter((row) => row.community !== communityName).map((row) => row.score);
  const competitorAverage = competitorScores.length
    ? Math.round(competitorScores.reduce((total, score) => total + score, 0) / competitorScores.length)
    : null;
  const score = unwrap(structure.score);
  const summary = unwrap(structure.summary);
  const overallPerformance = unwrap(structure.overall_performance);
  const summaryText =
    typeof summary === 'string' ? summary : typeof overallPerformance === 'string' ? overallPerformance : null;

  return {
    rankingRows,
    data: {
      ranking: primaryRow?.rank ?? null,
      strengths: asStringArray(unwrap(structure.competitive_advantages) ?? unwrap(structure.strengths)).slice(
        0,
        SPEED_TO_LEAD_SUMMARY_MAX_ITEMS
      ),
      limitations: asStringArray(
        unwrap(structure.areas_for_improvement) ?? unwrap(structure.weaknesses) ?? unwrap(structure.opportunities)
      ).slice(0, SPEED_TO_LEAD_SUMMARY_MAX_ITEMS),
      summary: summaryText,
      overall_score: primaryRow?.score ?? (typeof score === 'number' ? score : null),
      competitor_average: competitorAverage,
      related_data: [],
    },
  };
};
